import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useToast } from '@/hooks/use-toast'
import type { TenantUser, LoginForm } from '@/types'
import apiClient from '@/lib/api/client'
import { tenantLogin, getCurrentUser1, logout } from '@/lib/api/generated/sdk.gen'

// 登录
export const useLogin = () => {
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const { toast } = useToast()

  const login = async (data: LoginForm) => {
    setIsLoading(true)
    try {
      const res = await tenantLogin({ client: apiClient, body: { username: data.username, password: data.password }, responseStyle: 'fields', throwOnError: false })
      const status = (res as any)?.response?.status
      if (status && status !== 200) {
        const msg = ((res as any)?.error as any)?.message || '用户名或密码错误'
        throw new Error(msg)
      }
      const payload = (res as any)?.data
      const token = payload?.accessToken || payload?.token || payload?.data?.accessToken || payload?.data?.token
      if (!token) throw new Error('用户名或密码错误')
      localStorage.setItem('tenant_token', token)
      // 登录后立即拉取当前用户
      try {
        const me = await getCurrentUser1({ client: apiClient, responseStyle: 'fields', throwOnError: false })
        const meData: any = (me as any)?.data ?? me
        // 通过自定义事件让全局 AuthProvider 刷新
        window.dispatchEvent(new CustomEvent('auth-user-updated', { detail: meData }))
      } catch (_) {
        // 忽略，走后续路由守卫触发的刷新
      }
      toast({ title: '登录成功', description: '欢迎回来' })
      navigate('/dashboard')
      // 登录成功后才触发auth-change事件
      window.dispatchEvent(new Event('auth-change'))
    } catch (error: any) {
      // 不显示toast，让调用方处理错误显示
      throw error
    } finally {
      setIsLoading(false)
      // 移除这里的auth-change事件，避免登录失败时触发页面刷新
    }
  }

  return { mutate: login, isLoading }
}

// 登出
export const useLogout = () => {
  const navigate = useNavigate()
  const { toast } = useToast()
  return () => {
    logout({ client: apiClient, responseStyle: 'data' }).finally(() => {})
    localStorage.removeItem('tenant_token')
    window.dispatchEvent(new Event('auth-change'))
    toast({ title: '已退出登录' })
    navigate('/')
  }
}

// 获取当前用户信息
export const useCurrentUser = () => {
  const [user, setUser] = useState<TenantUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('tenant_token')
    if (!token) { setIsLoading(false); return }
    getCurrentUser1({ client: apiClient, responseStyle: 'fields', throwOnError: false })
      .then((res) => {
        const data: any = (res as any)?.data ?? res
        if (data && typeof data === 'object') setUser(data as TenantUser)
        else setUser(null)
      })
      .catch(() => setUser(null))
      .finally(() => setIsLoading(false))
  }, [])

  // 监听 storage 变化，以便在登录后立即更新状态
  useEffect(() => {
    const handleStorageChange = () => {
      const token = localStorage.getItem('tenant_token')
      if (token) {
        // 重新拉取当前用户
        getCurrentUser1({ client: apiClient, responseStyle: 'fields', throwOnError: false })
          .then((res) => {
            const data: any = (res as any)?.data ?? res
            setUser((data || null) as TenantUser | null)
          })
          .catch(() => setUser(null))
      } else {
        setUser(null)
      }
    }

    // 监听自定义事件，用于同步状态更新
    const handleAuthChange = () => {
      const token = localStorage.getItem('tenant_token')
      if (token) {
        getCurrentUser1({ client: apiClient, responseStyle: 'fields', throwOnError: false })
          .then((res) => {
            const data: any = (res as any)?.data ?? res
            setUser((data || null) as TenantUser | null)
          })
          .catch(() => setUser(null))
      } else {
        setUser(null)
      }
    }

    const handleAuthUserUpdated = (e: any) => {
      const detail = e?.detail
      if (detail) setUser(detail as TenantUser)
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('auth-change', handleAuthChange)
    window.addEventListener('auth-user-updated', handleAuthUserUpdated as EventListener)
    
    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('auth-change', handleAuthChange)
      window.removeEventListener('auth-user-updated', handleAuthUserUpdated as EventListener)
    }
  }, [])

  return {
    data: user,
    isLoading,
  }
}

// 主要的 useAuth hook
export const useAuth = () => {
  const { data: user, isLoading } = useCurrentUser()
  const logoutMutation = useLogout()
  
  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    logout: logoutMutation,
  }
}

// 检查是否已登录
export const useIsAuthenticated = () => {
  const { data: user, isLoading } = useCurrentUser()
  return {
    isAuthenticated: !!user,
    user,
    isLoading,
  }
}

// 修改密码 (Mock)
export const useChangePassword = () => {
  const [isLoading, setIsLoading] = useState(false)

  const changePassword = async (data: { oldPassword: string; newPassword: string }) => {
    setIsLoading(true)
    // 模拟 API 调用
    await new Promise(resolve => setTimeout(resolve, 1000))
    setIsLoading(false)
    // Mock 成功
    return Promise.resolve()
  }

  return {
    mutate: changePassword,
    isLoading,
  }
}